import {
  appFormUtil,
  gqlCacheUtil
} from 'modules/common';
import React, { useEffect } from 'react';

import {
  useSaveMetaInspectionMutation,
  SaveMetaInspection_Input,
  SavePerson_Input,
  useMetaInspectionFormQuery,
  ClientReportDetailSectionQuery,
  SaveMetaInspectionMutation,
  ClientReportDetailSectionDocument
} from 'generated/graphql';

export type IFormState = {
  id: string;
  included: boolean;
  inspector: SavePerson_Input | null;
  notesForClient: string;
};

const initialState: IFormState = {
  id: '',
  included: true,
  inspector: {
    id: '',
    firstName: '',
    lastName: ''
  },
  notesForClient: ''
};

export function useFormState(id: string) {
  const { formState, dispatch, genFieldProps } = appFormUtil.useFormState({
    initialState,
    key2FieldConfig: {
      id: {
        label: 'Id',
        type: 'text'
      },
      included: {
        label: 'Included',
        type: 'text'
      },
      inspector: {
        label: 'Inspector',
        type: 'text'
      },
      notesForClient: {
        label: 'Notes for client',
        type: 'text'
      }
    }
  });

  const { data, loading, called } = useQuery(id);

  useEffect(() => {
    if (formState.id === '' && data.id !== '' && !loading && called) {
      dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          ...formState,
          ...data
        }
      });
    }
  }, [
    called,
    data,
    dispatch,
    formState,
    loading
  ]);

  return React.useMemo(
    () => {

      return {
        formState,
        dispatch,
        genFieldProps
      };
    },
    [
      dispatch,
      formState,
      genFieldProps
    ]
  );
}

export interface IData extends Omit<IFormState, 'inspector'> {}

export function useQuery(id: string) {
  const tuple = useMetaInspectionFormQuery({
    variables: {
      id
    },
    skip: id === ''
  });

  return React.useMemo(
    () => {
      const data: IData = {
        id: tuple.data?.metaInspection?.id ?? '',
        included: tuple.data?.metaInspection?.included ?? true,
        notesForClient: tuple.data?.metaInspection?.notesForClient ?? ''
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}

export function useSaveMetaInspection() {
  const [
    fnMutate,
    tuple
  ] = useSaveMetaInspectionMutation({
    refetchQueries: []
  });
  const fnWrappedMutate = React.useCallback(
    async (input: SaveMetaInspection_Input) => {
      await fnMutate({
        variables: {
          input
        },
        updateQueries: {
          ...gqlCacheUtil.genUpdateQueryMap<ClientReportDetailSectionQuery, SaveMetaInspectionMutation>(ClientReportDetailSectionDocument, (prevState, { mutationResult }) => {
            if (!prevState.clientReport?.id) {
              return prevState;
            }
            if (!prevState.clientReport?.inspections) {
              return prevState;
            }
            if (!mutationResult.data?.saveMetaInspection) {
              return prevState;
            }
            const updatedEvents = prevState.clientReport.inspections.map((node) => {
              if (node.id === mutationResult.data?.saveMetaInspection?.id) {
                return {
                  ...node,
                  ...mutationResult.data.saveMetaInspection
                };
              }
              return node;
            });
            return {
              ...prevState,
              clientReport: {
                ...prevState.clientReport,
                inspections: updatedEvents
              }
            };
          })
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}
